import * as z from 'zod';

import { DURATION_UNIT, MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import { validationMessagesMap } from 'src/utils/messages';
import { entityFormFieldSchema } from 'src/features/entity/formSchema';
import { IDENTIFIER_TYPE } from 'src/features/membership/types';
import { PERIOD_TYPE } from 'src/features/stay/types';

export type MembershipTypeFormSchemaValues = z.infer<typeof membershipTypeFormSchema>;
export const membershipTypeFormSchema = z
    .object({
        translations: z
            .array(
                z.object({
                    language: z.string().nonempty(),
                    name: z.string().nonempty(),
                    description: z.string().nullish(),
                }),
            )
            .min(1),
        category: z.enum(MEMBERSHIP_TEMPLATE_CATEGORY),
        site: z.string(),
        has_pricing_plan: z.boolean().nullish(),
        has_time_restriction: z.boolean().nullish(),
        pricing_plan_identity: z.string().nullish(),
        apply_pricing_per_space: z.boolean().nullish(),
        allow_custom_space_count_definition: z.boolean().nullish(),
        limit_duration: z.boolean().nullish(),
        duration_unit: z.enum(DURATION_UNIT).nullish(),
        duration: z.number().int().min(1).nullish(),
        max_allowed_spaces_count: z.number().int().min(1).nullish(),
        allow_access_over_capacity_limit: z.boolean().nullish(),
        contribute_to_available_spaces: z.boolean().nullish(),
        allow_extra_visitors: z.boolean().nullish(),
        max_extra_visitors_count: z.number().int().min(1).nullish(),
        auto_renew: z.boolean().nullish(),
        allow_access_as_client_outside_restrictions: z.boolean().nullish(),
        limit_to_roles: z.array(z.string()).nullish(),
        custom_duration: z.boolean().nullish(),
        has_allowed_spaces_count: z.boolean().nullish(),
    })
    .superRefine((data, ctx) => {
        if (data.has_pricing_plan === true && !data.pricing_plan_identity) {
            ctx.addIssue({
                code: 'custom',
                path: ['pricing_plan_identity'],
                message: validationMessagesMap.validationErrorRequired,
            });
        }
        if (
            data.limit_duration === true &&
            (data.custom_duration === false || data.custom_duration === undefined || data.custom_duration === null)
        ) {
            if (data.duration_unit === null || data.duration_unit === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['duration_unit'],
                    message: validationMessagesMap.validationErrorRequired,
                });
            }
            if (data.duration === null || data.duration === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['duration'],
                    message: validationMessagesMap.validationErrorRequired,
                });
            }
        }
        if (data.allow_extra_visitors === true && !data.max_extra_visitors_count) {
            ctx.addIssue({
                code: 'custom',
                path: ['max_extra_visitors_count'],
                message: validationMessagesMap.validationErrorRequired,
            });
        }

        if (
            data.has_allowed_spaces_count === true &&
            (data.allow_custom_space_count_definition === false ||
                data.allow_custom_space_count_definition === undefined)
        ) {
            if (data.max_allowed_spaces_count === null || data.max_allowed_spaces_count === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['max_allowed_spaces_count'],
                    message: validationMessagesMap.validationErrorRequired,
                });
            }
        }
    });

export type MembershipIdentifierFormSchemaValues = z.infer<typeof membershipIdentifierFormSchema>;
export const membershipIdentifierFormSchema = entityFormFieldSchema.extend({
    identifier: z.object({
        value: z.string(),
        type: z.enum(IDENTIFIER_TYPE),
    }),
    public_notes: z.string().nullish(),
    private_notes: z.string().nullish(),
});

export type RestrictedGatewayFormSchemaValues = z.infer<typeof restrictedGatewayFormSchema>;
export const restrictedGatewayFormSchema = z.object({
    gatewayId: z.string(),
});

// Factory: when the membership template has "limit spaces" on ("has_allowed_spaces_count"), a STAY rule
// must specify the allowed spaces count, so the field becomes required.
export const getZoneRuleFormSchema = (hasAllowedSpacesCount = false) =>
    z
        .object({
            zone: z.string(),
            allowed_stay_type: z.enum(PERIOD_TYPE),
            transit_time_minutes: z.number().int().min(1).nullish(),
            allowed_spaces_count: z.number().int().min(0).nullish(),
            reserve_spaces_count: z.number().int().min(0).nullish(),
            extra_visitors_count: z.number().int().min(0).nullish(),
            allowed_extra_visitors_time_minutes: z.number().int().min(0).nullish(),
        })
        .superRefine((data, ctx) => {
            if (
                hasAllowedSpacesCount &&
                data.allowed_stay_type === PERIOD_TYPE.STAY &&
                (data.allowed_spaces_count === null || data.allowed_spaces_count === undefined)
            ) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['allowed_spaces_count'],
                    message: validationMessagesMap.validationErrorRequired,
                });
            }
            if (data.allowed_stay_type === PERIOD_TYPE.TRANSIT && data.transit_time_minutes === null) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['transit_time_minutes'],
                    message: validationMessagesMap.validationErrorRequired,
                });
            }
        })
        .transform((data) => {
            if (data.allowed_stay_type === PERIOD_TYPE.TRANSIT) {
                return {
                    ...data,
                    allowed_spaces_count: null,
                    reserve_spaces_count: null,
                    extra_visitors_count: null,
                    allowed_extra_visitors_time_minutes: null,
                };
            }
            if (data.allowed_stay_type === PERIOD_TYPE.STAY) {
                return {
                    ...data,
                    transit_time_minutes: null,
                };
            }
            return data;
        });

export type ZoneRuleFormSchemaValues = z.infer<ReturnType<typeof getZoneRuleFormSchema>>;
